import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { TopBar } from '../components/TopBar'
import { useApp } from '../store/AppContext'
import {
  ONBOARDING_REQUIREMENTS,
  getOnboardingRecord,
  onboardingProgress,
  saveOnboardingRecord,
} from '../lib/onboarding'
import {
  CREDENTIAL_TYPES,
  credentialStatus,
  credentialsForWorker,
  reviewCredential,
  saveCredential,
} from '../lib/credentials'

const CASEY_ID = 'u-casey'

function ensureLeoComplianceDemo() {
  const record = getOnboardingRecord(CASEY_ID)
  if (record.completedItemIds.length === 0 && !record.signedName) {
    saveOnboardingRecord({
      ...record,
      completedItemIds: ONBOARDING_REQUIREMENTS.slice(0, 5).map((item) => item.id),
      acknowledged: true,
      signedName: 'Casey Morgan',
    })
  }

  const existing = credentialsForWorker(CASEY_ID)
  const addIfMissing = (type: Parameters<typeof saveCredential>[0]['type'], label: string, patch: Partial<Parameters<typeof saveCredential>[0]>) => {
    if (existing.some((item) => item.type === type)) return
    saveCredential({ id: `leo-casey-${type}`, userId: CASEY_ID, type, label, privacyChecked: true, uploadedAt: new Date().toISOString(), ...patch })
  }
  addIfMissing('ndis_screening', 'NDIS Worker Screening', { referenceNumber: 'NDIS-WS-24831', expiryDate: '2028-06-30', reviewedAt: new Date().toISOString(), reviewedByUserId: 'u-lillian' })
  addIfMissing('wwcc', "Working With Children's Check", { referenceNumber: 'WWCC-94821', expiryDate: '2029-03-15', reviewedAt: new Date().toISOString(), reviewedByUserId: 'u-lillian' })
  addIfMissing('drivers_licence', "Driver's Licence", { fileName: 'Casey Drivers Licence.pdf', expiryDate: '2029-11-10', reviewedAt: new Date().toISOString(), reviewedByUserId: 'u-lillian' })
  addIfMissing('first_aid', 'First Aid', { fileName: 'First Aid Certificate.pdf', expiryDate: '2027-05-12' })
  addIfMissing('cpr', 'CPR', { fileName: 'CPR Certificate.pdf', expiryDate: '2027-01-20' })
}

export function LeoStaffOnboarding() {
  const { currentUser, allUsers } = useApp()
  const [, refresh] = useState(0)
  const isCasey = currentUser.id === CASEY_ID
  const isLillian = currentUser.id === 'u-lillian'

  useEffect(() => {
    ensureLeoComplianceDemo()
    const rerender = () => refresh((value) => value + 1)
    window.addEventListener('schub-onboarding-updated', rerender)
    window.addEventListener('schub-credentials-updated', rerender)
    rerender()
    return () => {
      window.removeEventListener('schub-onboarding-updated', rerender)
      window.removeEventListener('schub-credentials-updated', rerender)
    }
  }, [])

  if (!isCasey && !isLillian) return <div className="p-5"><Link to="/youth-workspace" className="font-black text-violet-700">← Leo’s workspace</Link></div>

  const record = getOnboardingRecord(CASEY_ID)
  const progress = onboardingProgress(record)
  const credentials = credentialsForWorker(CASEY_ID)

  const toggleRequirement = (id: string) => {
    if (!isCasey) return
    const completedItemIds = record.completedItemIds.includes(id)
      ? record.completedItemIds.filter((itemId) => itemId !== id)
      : [...record.completedItemIds, id]
    saveOnboardingRecord({ ...record, completedItemIds, completedAt: undefined })
  }

  const updateAcknowledgement = (patch: { acknowledged?: boolean; signedName?: string }) => {
    if (!isCasey) return
    saveOnboardingRecord({ ...record, ...patch, completedAt: undefined })
  }

  const completeOnboarding = () => {
    if (!isCasey || progress !== 100 || !record.acknowledged || record.signedName.trim().length < 2) return
    saveOnboardingRecord({ ...record, completedAt: new Date().toISOString() })
  }

  const reviewerName = (id?: string) => id ? allUsers.find((user) => user.id === id)?.name ?? 'Authorised reviewer' : ''

  return (
    <div className="min-h-screen bg-slate-50 pb-28">
      <TopBar
        title={isCasey ? 'My Leo Onboarding & Compliance' : 'Leo Staff Onboarding & Compliance'}
        subtitle={isCasey ? 'Casey · participant-specific worker requirements' : 'Lillian · parent / Plan Nominee admin review'}
      />
      <div className="flex flex-col gap-4 px-4 py-4">
        <Link to={isCasey ? '/youth-workspace/more' : '/youth-workspace/more-admin'} className="w-fit rounded-xl bg-violet-100 px-3 py-2 text-sm font-black text-violet-700">← More</Link>

        <section className="rounded-3xl border border-violet-200 bg-violet-50 p-4">
          <p className="text-xs font-black uppercase tracking-wide text-violet-700">Connected compliance workflow</p>
          <p className="mt-2 text-sm font-semibold text-violet-950">
            {isCasey
              ? 'Complete Leo-specific reading, training, safety information, acknowledgement and supplied worker credentials here. Lillian can see the completion status from her admin view.'
              : 'This is the admin side of Casey’s Leo onboarding. Lillian can see what Casey has completed, what is still pending, supplied credentials, expiry status and which items have been reviewed.'}
          </p>
        </section>

        <section className="rounded-3xl bg-white p-4 shadow-card">
          <div className="flex items-center justify-between gap-3"><div><p className="font-black text-slate-900">Casey Morgan</p><p className="text-xs font-semibold text-slate-500">Independent Support Worker · Leo’s Team</p></div><span className={`rounded-full px-3 py-1 text-xs font-black ${record.completedAt ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>{record.completedAt ? 'Completed' : 'In progress'}</span></div>
          <div className="mt-4 flex items-end justify-between"><div><p className="text-xs font-black uppercase text-slate-400">Onboarding progress</p><p className="mt-1 text-sm font-semibold text-slate-600">{record.completedItemIds.length} of {ONBOARDING_REQUIREMENTS.length} requirements</p></div><p className="text-2xl font-black text-violet-700">{progress}%</p></div>
          <div className="mt-3 h-3 overflow-hidden rounded-full bg-slate-100"><div className="h-full rounded-full bg-violet-500" style={{ width: `${progress}%` }} /></div>
        </section>

        <section className="rounded-3xl bg-white p-4 shadow-card">
          <h2 className="font-black text-slate-900">Leo-specific onboarding requirements</h2>
          <div className="mt-3 grid gap-2">
            {ONBOARDING_REQUIREMENTS.map((item) => {
              const complete = record.completedItemIds.includes(item.id)
              return <button key={item.id} type="button" disabled={!isCasey} onClick={() => toggleRequirement(item.id)} className="flex w-full items-start gap-3 rounded-2xl bg-slate-50 p-3 text-left disabled:cursor-default"><span className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full border text-xs font-black ${complete ? 'border-emerald-500 bg-emerald-500 text-white' : 'border-slate-300 text-slate-400'}`}>{complete ? '✓' : ''}</span><span className="flex-1"><span className="block text-sm font-bold text-slate-900">{item.title}</span><span className="mt-1 block text-xs text-slate-500">{item.description}</span><span className={`mt-2 block text-xs font-black ${complete ? 'text-emerald-600' : 'text-amber-600'}`}>{complete ? 'Reviewed / completed' : 'Pending'}</span></span></button>
            })}
          </div>
        </section>

        <section className="rounded-3xl bg-white p-4 shadow-card">
          <h2 className="font-black text-slate-900">Worker acknowledgement & sign-off</h2>
          {isCasey ? <>
            <label className="mt-3 flex items-start gap-3 text-sm font-semibold text-slate-700"><input type="checkbox" checked={record.acknowledged} onChange={(e) => updateAcknowledgement({ acknowledged: e.target.checked })} className="mt-1" />I confirm I have reviewed the Leo-specific onboarding information assigned to me.</label>
            <input value={record.signedName} onChange={(e) => updateAcknowledgement({ signedName: e.target.value })} placeholder="Type full name" className="mt-3 w-full rounded-xl border border-slate-200 px-3 py-3 text-sm" />
            <button onClick={completeOnboarding} disabled={progress !== 100 || !record.acknowledged || record.signedName.trim().length < 2} className="mt-3 w-full rounded-xl bg-violet-600 py-3 text-sm font-black text-white disabled:bg-slate-200 disabled:text-slate-400">{record.completedAt ? 'Onboarding completed ✓' : 'Complete onboarding'}</button>
          </> : <div className="mt-3 grid grid-cols-2 gap-3"><div className="rounded-2xl bg-slate-50 p-3"><p className="text-[10px] font-black uppercase text-slate-400">Acknowledged</p><p className="mt-1 font-black text-slate-900">{record.acknowledged ? 'Yes ✓' : 'No'}</p></div><div className="rounded-2xl bg-slate-50 p-3"><p className="text-[10px] font-black uppercase text-slate-400">Signed name</p><p className="mt-1 font-black text-slate-900">{record.signedName || 'Pending'}</p></div></div>}
        </section>

        <section className="rounded-3xl bg-white p-4 shadow-card">
          <h2 className="font-black text-slate-900">Worker compliance documents</h2>
          <p className="mt-1 text-xs text-slate-500">Prototype compliance tracking. “Reviewed” means an authorised person has visually checked the supplied information; Marnixia is not independently validating the credential.</p>
          <div className="mt-3 grid gap-2">
            {CREDENTIAL_TYPES.map((meta) => {
              const credential = credentials.find((item) => item.type === meta.type)
              const status = credential ? credentialStatus(credential) : 'Missing'
              return <div key={meta.type} className="rounded-2xl bg-slate-50 p-3"><div className="flex items-start justify-between gap-3"><div><p className="text-sm font-bold text-slate-900">{meta.label}</p><p className="mt-1 text-xs font-semibold text-slate-500">{status}</p>{credential?.expiryDate && <p className="mt-1 text-xs text-slate-500">Expiry: {new Date(`${credential.expiryDate}T00:00:00`).toLocaleDateString()}</p>}{credential?.reviewedAt && <p className="mt-1 text-xs font-black text-emerald-600">Reviewed by {reviewerName(credential.reviewedByUserId)}</p>}</div>{isLillian && credential && !credential.reviewedAt && <button onClick={() => reviewCredential(credential.id, currentUser.id)} className="rounded-lg bg-violet-600 px-3 py-2 text-xs font-black text-white">Mark reviewed</button>}</div></div>
            })}
          </div>
        </section>
      </div>
    </div>
  )
}

